import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, RendererFactory2 } from '@angular/core';
import { EventManager } from '@angular/platform-browser';

import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';

import { EnvironmentService } from '../environment';
import { WindowRefService } from '../window-ref/window-ref.service';

type MobileAppWindow = Window & {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  MobileBridge: {
    dispatchEvent: (event: MobileAppEvents, value: string) => {};
  };
};

export enum MobileAppEvents {
  TOGGLE_NATIVE_SMARTBAR = 'mobile-app.footer-set-visibility.v1',
  TOGGLE_NATIVE_TABS = 'mobile-app.navigation-tab-set-visibility.v1',
  PRODUCT_NAVIGATION = 'mobile-app.product-menu-navigate.v1'
}

export type MobileAppEventsValue = Record<string, unknown>;

@Injectable({
  providedIn: 'root'
})
export class MobileAppBridgeService {
  public debounceTime = 10;

  private readonly win: MobileAppWindow;
  private readonly toggleNativeSmartbar$ = new Subject<boolean>();
  private readonly toggleNativeTabs$ = new Subject<boolean>();

  constructor(
    private readonly envService: EnvironmentService,
    private readonly eventManager: EventManager,
    private readonly rendererFactory: RendererFactory2,
    { nativeWindow }: WindowRefService<MobileAppWindow>,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.win = nativeWindow;

    if (this.envService.mobileApp) {
      this.setHelperClass();
      this.addUnloadListener();
      this.subscribeToToggleNativeSmartbar();
      this.subscribeToToggleNativeTabs();
    }
  }

  showNativeSmartbar(): void {
    this.toggleNativeSmartbar$.next(true);
  }

  hideNativeSmartbar(): void {
    this.toggleNativeSmartbar$.next(false);
  }

  showNativeTabs(): void {
    this.toggleNativeTabs$.next(true);
  }

  hideNativeTabs(): void {
    this.toggleNativeTabs$.next(false);
  }

  /**
   * open the product navigation inside the mobile app
   */
  openProductNavigation(): void {
    this.sendProductNavigationEvent();
  }

  private toggleNativeSmartbar(showNativeSmartbar: boolean): void {
    this.dispatchEvent(MobileAppEvents.TOGGLE_NATIVE_SMARTBAR, { visible: showNativeSmartbar });
  }

  private toggleNativeTabs(showNativeTabs: boolean): void {
    this.dispatchEvent(MobileAppEvents.TOGGLE_NATIVE_TABS, { visible: showNativeTabs });
  }

  private dispatchEvent(eventName: MobileAppEvents, eventVal: MobileAppEventsValue): void {
    this.envService.mobileApp && this.win.MobileBridge?.dispatchEvent(eventName, JSON.stringify(eventVal));
  }

  private setHelperClass(): void {
    const renderer = this.rendererFactory.createRenderer(null, null);
    renderer.addClass(this.document.body, 'is-mobile-app');
  }

  private addUnloadListener(): void {
    this.eventManager.addEventListener(this.win as unknown as HTMLElement, 'unload', () => {
      this.toggleNativeSmartbar(true);
    });
  }

  private subscribeToToggleNativeSmartbar(): void {
    this.toggleNativeSmartbar$
      .pipe(debounceTime(this.debounceTime), distinctUntilChanged())
      .subscribe(showNativeSmartbar => this.toggleNativeSmartbar(showNativeSmartbar));
  }

  private subscribeToToggleNativeTabs(): void {
    this.toggleNativeTabs$.subscribe(showNativeTabs => this.toggleNativeTabs(showNativeTabs));
  }

  private sendProductNavigationEvent(): void {
    this.dispatchEvent(MobileAppEvents.PRODUCT_NAVIGATION, {});
  }
}
